import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { AccountsService } from '../../accounts.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ApMatchingPolicy, ApMatchingTolerance } from '../phase7-ap-controls.model';

@Component({
    selector: 'app-ap-matching-policies',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, ButtonModule, CheckboxModule, InputNumberModule, InputTextModule, SelectModule, TableModule, TagModule, ToastModule, PageShellComponent, PageHeroComponent, PageFormSectionComponent, PageDataPanelComponent],
    providers: [MessageService],
    templateUrl: './matching-policies.component.html',
    styleUrls: ['./matching-policies.component.css']
})
export class MatchingPoliciesComponent implements OnInit {
    companyId = 0;
    policies: ApMatchingPolicy[] = [];
    tolerances: ApMatchingTolerance[] = [];
    loading = false;

    policy: ApMatchingPolicy = {};
    tolerance: ApMatchingTolerance = {};

    matchTypes = [
        { label: 'Not required', value: 'NOT_REQUIRED' },
        { label: 'Two-way', value: 'TWO_WAY' },
        { label: 'Three-way', value: 'THREE_WAY' }
    ];
    components = ['TOTAL', 'LINE_TOTAL', 'QUANTITY', 'PRICE', 'CHARGES', 'TAX'].map(value => ({ label: value.replace('_', ' '), value }));
    currencies = ['SAR', 'USD', 'EUR', 'AED'].map(value => ({ label: value, value }));

    constructor(
        private accountsService: AccountsService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        this.resetPolicy();
        this.resetTolerance();
        this.load();
    }

    load(): void {
        if (!this.companyId) return;
        this.loading = true;
        this.accountsService.getApMatchingPolicies(this.companyId).subscribe({
            next: policies => {
                this.policies = policies ?? [];
                this.accountsService.getApMatchingTolerances(this.companyId).subscribe({
                    next: tolerances => {
                        this.tolerances = tolerances ?? [];
                        this.loading = false;
                    },
                    error: err => this.fail('Tolerance load failed', err)
                });
            },
            error: err => this.fail('Policy load failed', err)
        });
    }

    savePolicy(): void {
        this.policy.owningCompanyId = this.companyId;
        this.policy.updatedBy = this.actorId();
        this.policy.createdBy = this.policy.createdBy ?? this.actorId();
        this.accountsService.saveApMatchingPolicy(this.policy).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Matching policy saved.' });
                this.resetPolicy();
                this.load();
            },
            error: err => this.fail('Policy save failed', err)
        });
    }

    saveTolerance(): void {
        this.tolerance.owningCompanyId = this.companyId;
        this.accountsService.saveApMatchingTolerance(this.tolerance).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Tolerance saved.' });
                this.resetTolerance();
                this.load();
            },
            error: err => this.fail('Tolerance save failed', err)
        });
    }

    editPolicy(row: ApMatchingPolicy): void {
        this.policy = { ...row };
    }

    editTolerance(row: ApMatchingTolerance): void {
        this.tolerance = { ...row };
    }

    resetPolicy(): void {
        this.policy = { owningCompanyId: this.companyId, matchType: 'TWO_WAY', active: true, requireReceiptEvidence: false };
    }

    resetTolerance(): void {
        this.tolerance = { owningCompanyId: this.companyId, matchComponent: 'TOTAL', amountTolerance: 0, percentageTolerance: 0, active: true };
    }

    private fail(summary: string, err: any): void {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary, detail: err.error?.message || 'Operation failed.' });
    }

    private actorId(): number | undefined {
        const user = this.sessionStorageService.getUser();
        return user?.id ?? user?.userId ?? this.sessionStorageService.getCustomer()?.id;
    }
}
